// Shared types for pplTalk messages, users, and interactions

export interface FirebaseTimestamp {
  seconds: number;
  nanoseconds: number;
  // callable responses serialize Firestore timestamps with underscored keys
  _seconds?: number;
  _nanoseconds?: number;
  toDate?: () => Date;
}

export type TimestampLike = Date | FirebaseTimestamp | number | string | null | undefined;

export interface PplTalkMessage {
  id: string;
  title?: string;
  body?: string;
  categories?: string[];
  userName?: string;
  tetherHandle?: string;
  tetherHAndle?: string; // Note: typo exists in DB
  userAvatar?: string;
  profileImageUrl?: string;
  likes?: number;
  likedBy?: string[];
  replies?: number;
  timestamp?: TimestampLike;
  rank?: number;
  userId?: string;
  flagCount?: number;
}

export interface PplTalkReply {
  id: string;
  text: string;
  userName?: string;
  tetherHandle?: string;
  tetherHAndle?: string;
  profileImageUrl?: string;
  likes?: number;
  likedBy?: string[];
  timestamp?: TimestampLike;
  userId?: string;
  originalMessageId?: string;
}

export interface UserProfile {
  uid?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  tetherHandle?: string;
  tetherHAndle?: string;
  profileImageUrl?: string;
  avatar?: string;
  city?: string;
  country?: string;
  about?: string;
  interests?: string[];
  lookingFor?: string;
  gender?: string;
  sexualOrientation?: string;
  showAge?: boolean;
  showSexualOrientation?: boolean;
  dateOfBirth?: TimestampLike;
  createdAt?: TimestampLike;
  selectedPlan?: string;
}

// Cloud Function response types
export interface CloudFunctionResponse<T = unknown> {
  data: T;
}

export interface TalkValidatorResponse {
  success: boolean;
  message?: string;
  reason?: string;
  rank?: number;
}

export interface LikeMessageResponse {
  success: boolean;
  likes: number;
  likedBy: string[];
}

export interface ReplyValidatorResponse {
  success: boolean;
  message?: string;
  reason?: string;
  rank?: number;
}

export interface FlagMessageResponse {
  success: boolean;
  message?: string;
}

export interface GetPplTalkMessagesResponse {
  success: boolean;
  messages: PplTalkMessage[];
  hasMore: boolean;
  lastMessageId: string | null;
  error?: string;
}

export interface GetPplTalkMessageResponse {
  success: boolean;
  message: PplTalkMessage;
  replies: PplTalkReply[];
  error?: string;
}

// ─── Public Profile types ────────────────────────────────────────────────────

export interface PublicProfileData {
  id: string;
  tetherHandle?: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  city?: string;
  country?: string;
  gender?: string;
  dateOfBirth?: string;
  showAge?: boolean;
  sexualOrientation?: string;
  showSexualOrientation?: boolean;
  lookingFor?: string;
  aboutMe?: string;
  interests?: string | string[];
  interestedIn?: string;
  lastActive?: TimestampLike;
  isLocationVerified?: boolean;
  /** Server-stamped place the flag was minted for; absent on legacy docs. */
  verifiedCity?: string;
  verifiedCountry?: string;
  /** Client-writable — never grants a badge. Read isLocationVerifiedFor(). */
  locationValidation?: { isValid?: boolean };
  profileSet?: boolean;
}

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

export interface UserBadge {
  earned: boolean;
  earnedAt?: TimestampLike;
}

export interface OnlineStatusData {
  success: boolean;
  isOnline: boolean;
  status: string;
  lastActive?: TimestampLike;
}

// ─── Cloud Function response types for profiles ────────────────────────────

export interface GetPublicProfileResponse {
  id: string;
  tetherHandle?: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  city?: string;
  country?: string;
  gender?: string;
  dateOfBirth?: string;
  showAge?: boolean;
  sexualOrientation?: string;
  showSexualOrientation?: boolean;
  lookingFor?: string;
  aboutMe?: string;
  interests?: string | string[];
  interestedIn?: string;
  lastActive?: TimestampLike;
  isLocationVerified?: boolean;
  /** Server-stamped place the flag was minted for; absent on legacy docs. */
  verifiedCity?: string;
  verifiedCountry?: string;
  /** Client-writable — never grants a badge. Read isLocationVerifiedFor(). */
  locationValidation?: { isValid?: boolean };
  profileSet?: boolean;
}

export interface GetUserBadgesResponse {
  success: boolean;
  badges: Record<string, UserBadge>;
  badgeDefinitions: Record<string, BadgeDefinition>;
}

// ─── Find People types ──────────────────────────────────────────────────────

export interface DiscoveredUser {
  id: string;
  uid?: string;
  firstName?: string;
  lastName?: string;
  handle?: string;
  name?: string;
  avatar?: string;
  interests?: string | string[];
  aboutMe?: string;
  contactType?: string;
  profileSet?: boolean;
  // Location-specific
  city?: string;
  country?: string;
  distance?: string;
  matchType?: string;
  // Interest-specific
  score?: number;
  // Online-specific
  status?: string;
  lastAppOpen?: TimestampLike;
}

export interface FindPplResponse {
  users: DiscoveredUser[];
  totalCount?: number;
  hasMore?: boolean;
  lastDocId?: string;
}

// Categories constant
export const PPLTALK_CATEGORIES = [
  'just talking',
  'relationships',
  'personal',
  'support',
  'check-ins',
  'wins',
  'gratitude',
  'advice wanted',
  'conflict & repair',
  'reflections',
  'life transitions',
  'general',
] as const;

export type PplTalkCategory = (typeof PPLTALK_CATEGORIES)[number];
